import {Fragment, useId, useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../app/rp_reducer";
import {getCityLink} from "../../utils/get_city_link";
import {getOfferPricesMonthLabel} from "../../utils/get_offer_prices_month_label";
import {getTransactionPriceRows} from "../../utils/get_transaction_price_rows";
import {getTransactionPricesGapNote, ITransactionPricesGapCity} from "../../utils/get_transaction_prices_gap_note";
import {getTransactionPricesSummary} from "../../utils/get_transaction_prices_summary";
import {PricesHubSchemaDataset} from "./PricesHubSchemaDataset";
import {PricesHubTransactionPricesCards} from "./PricesHubTransactionPricesCards";
import {PricesHubTransactionPricesTable} from "./PricesHubTransactionPricesTable";
import {PricesHubViewSectionTracker} from "./PricesHubViewSectionTracker";

type GapCityForm = "nominative" | "locative";

const renderGapCities = (cities: ITransactionPricesGapCity[], form: GapCityForm) =>
    cities.map((city, index) => (
        <Fragment key={city.slug}>
            {index > 0 ? " i " : null}
            <a href={getCityLink(city.slug)} className={gapCityLinkStyle}>
                {form === "locative" ? city.nameWhere : city.name}
            </a>{" "}
            ({city.differenceLabel})
        </Fragment>
    ));

export const PricesHubTransactionPrices = () => {
    const headingId = useId();
    const {isMobile} = useUserDevice();
    const citiesData = useSelector((store: IRPStore) => store.report.price.allCitiesData);
    const transactionPrices = useSelector((store: IRPStore) => store.report.price.transactionPrices);
    const citiesDataHistory = useSelector((store: IRPStore) => store.report.price.allCitiesDataHistory);

    const rows = useMemo(() => getTransactionPriceRows(citiesData, transactionPrices, citiesDataHistory), [citiesData, transactionPrices, citiesDataHistory]);
    const summary = useMemo(() => getTransactionPricesSummary(rows), [rows]);

    if (!summary) {
        return null;
    }

    const offerMonthLabel = getOfferPricesMonthLabel(citiesData);
    const gapNote = getTransactionPricesGapNote(summary);

    return (
        <section className={sectionStyle} aria-labelledby={headingId}>
            <PricesHubViewSectionTracker sectionName="transaction_prices" />

            <Container maxWidthVariant="lg">
                <div className={innerStyle}>
                    <Text id={headingId} as="h2" variant="headline_3" strong className={headingStyle}>
                        Ceny transakcyjne w Polsce – ile płacimy za mieszkania?
                    </Text>

                    <Text as="p" variant="body_copy_1" align="center" className={leadStyle}>
                        Cena z ogłoszenia to nie to samo, co kwota w akcie notarialnym. Poniżej zestawiamy średnie ceny ofertowe pochodzące z naszego portalu i
                        ceny transakcyjne nowych mieszkań w {rows.length} największych miastach.
                    </Text>

                    {isMobile ? (
                        <PricesHubTransactionPricesCards rows={rows} dominantPeriodLabel={summary.dominantPeriodLabel} offerMonthLabel={offerMonthLabel} />
                    ) : (
                        <PricesHubTransactionPricesTable rows={rows} dominantPeriodLabel={summary.dominantPeriodLabel} offerMonthLabel={offerMonthLabel} />
                    )}

                    {gapNote ? (
                        <div className={noteStyle}>
                            <span className={noteIconHolderStyle}>
                                <InfoIcon size="2.4" fill="currentColor" />
                            </span>

                            <div className={noteCopyStyle}>
                                <Text as="p" variant="info_txt_1" className={noteTextStyle}>
                                    Różnica między średnią ceną z ogłoszenia (cena ofertowa) a kwotą płaconą w akcie notarialnym (cena transakcyjna) jest dziś
                                    największa {gapNote.widestPreposition} {renderGapCities(gapNote.widestCities, "locative")} – to tam kupujący mają najwięcej
                                    miejsca na negocjacje.
                                    {gapNote.narrowestCities.length > 0 ? (
                                        <> Najmniejszą różnicę notują: {renderGapCities(gapNote.narrowestCities, "nominative")}.</>
                                    ) : null}
                                </Text>

                                <Text as="p" variant="info_txt_1" className={noteTextStyle}>
                                    Warto pamiętać, że w mniejszych miastach liczba rejestrowanych transakcji jest niższa, przez co średnie z RCN mają tendencję
                                    do zbliżania się do siebie. Minimalna różnica nie musi oznaczać, że negocjacje są niemożliwe – raczej że dane nie pokazują
                                    wyraźnego marginesu.
                                </Text>
                            </div>
                        </div>
                    ) : null}
                </div>
            </Container>

            <PricesHubSchemaDataset rows={rows} summary={summary} offerMonthLabel={offerMonthLabel} />
        </section>
    );
};

const sectionStyle = css({
    pt: 6,
    pb: 2,
    backgroundColor: "var(--colors-white)",

    md: {
        pt: 9,
        pb: 3
    }
});

const innerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    maxWidth: "127.2rem",
    mx: "auto",

    md: {
        gap: 3
    }
});

const headingStyle = css({
    width: "100%",
    margin: 0,
    color: "var(--colors-secondary)",
    textAlign: "left",

    md: {
        textAlign: "center"
    }
});

const leadStyle = css({
    maxWidth: "90rem",
    margin: 0,
    color: "var(--colors-gray-700)"
});

const noteStyle = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    width: "100%"
});

const noteIconHolderStyle = css({
    display: "inline-flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem",
    color: "var(--colors-gray-700)"
});

const noteCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const noteTextStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const gapCityLinkStyle = css({
    color: "inherit",
    textDecoration: "underline",

    _hover: {
        color: "var(--colors-secondary)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
import {Fragment, useId, useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../app/rp_reducer";
import {getCityLink} from "../../utils/get_city_link";
import {getOfferPricesMonthLabel} from "../../utils/get_offer_prices_month_label";
import {getTransactionPriceRows} from "../../utils/get_transaction_price_rows";
import {getTransactionPricesGapNote, ITransactionPricesGapCity} from "../../utils/get_transaction_prices_gap_note";
import {getTransactionPricesSummary} from "../../utils/get_transaction_prices_summary";
import {PricesHubSchemaDataset} from "./PricesHubSchemaDataset";
import {PricesHubTransactionPricesCards} from "./PricesHubTransactionPricesCards";
import {PricesHubTransactionPricesTable} from "./PricesHubTransactionPricesTable";
import {PricesHubViewSectionTracker} from "./PricesHubViewSectionTracker";

type GapCityForm = "nominative" | "locative";

const renderGapCities = (cities: ITransactionPricesGapCity[], form: GapCityForm) =>
    cities.map((city, index) => (
        <Fragment key={city.slug}>
            {index > 0 ? " i " : null}
            <a href={getCityLink(city.slug)} className={gapCityLinkStyle}>
                {form === "locative" ? city.nameWhere : city.name}
            </a>{" "}
            ({city.differenceLabel})
        </Fragment>
    ));

export const PricesHubTransactionPrices = () => {
    const headingId = useId();
    const {isMobile} = useUserDevice();
    const citiesData = useSelector((store: IRPStore) => store.report.price.allCitiesData);
    const transactionPrices = useSelector((store: IRPStore) => store.report.price.transactionPrices);
    const citiesDataHistory = useSelector((store: IRPStore) => store.report.price.allCitiesDataHistory);

    const rows = useMemo(() => getTransactionPriceRows(citiesData, transactionPrices, citiesDataHistory), [citiesData, transactionPrices, citiesDataHistory]);
    const summary = useMemo(() => getTransactionPricesSummary(rows), [rows]);

    if (!summary) {
        return null;
    }

    const offerMonthLabel = getOfferPricesMonthLabel(citiesData);
    const gapNote = getTransactionPricesGapNote(summary);

    return (
        <section className={sectionStyle} aria-labelledby={headingId}>
            <PricesHubViewSectionTracker sectionName="transaction_prices" />

            <Container maxWidthVariant="lg">
                <div className={innerStyle}>
                    <Text id={headingId} as="h2" variant="headline_3" strong className={headingStyle}>
                        Ceny transakcyjne w Polsce – ile płacimy za mieszkania?
                    </Text>

                    <Text as="p" variant="body_copy_1" align="center" className={leadStyle}>
                        Cena z ogłoszenia to nie to samo, co kwota w akcie notarialnym. Poniżej zestawiamy średnie ceny ofertowe pochodzące z naszego portalu i
                        ceny transakcyjne nowych mieszkań w {rows.length} największych miastach.
                    </Text>

                    {isMobile ? (
                        <PricesHubTransactionPricesCards rows={rows} dominantPeriodLabel={summary.dominantPeriodLabel} offerMonthLabel={offerMonthLabel} />
                    ) : (
                        <PricesHubTransactionPricesTable rows={rows} dominantPeriodLabel={summary.dominantPeriodLabel} offerMonthLabel={offerMonthLabel} />
                    )}

                    {gapNote ? (
                        <div className={noteStyle}>
                            <span className={noteIconHolderStyle}>
                                <InfoIcon size="2.4" fill="currentColor" />
                            </span>

                            <div className={noteCopyStyle}>
                                <Text as="p" variant="info_txt_1" className={noteTextStyle}>
                                    Różnica między średnią ceną z ogłoszenia (cena ofertowa) a kwotą płaconą w akcie notarialnym (cena transakcyjna) jest dziś
                                    największa {gapNote.widestPreposition} {renderGapCities(gapNote.widestCities, "locative")} – to tam kupujący mają najwięcej
                                    miejsca na negocjacje.
                                    {gapNote.narrowestCities.length > 0 ? (
                                        <> Najmniejszą różnicę notują: {renderGapCities(gapNote.narrowestCities, "nominative")}.</>
                                    ) : null}
                                </Text>

                                <Text as="p" variant="info_txt_1" className={noteTextStyle}>
                                    Warto pamiętać, że w mniejszych miastach liczba rejestrowanych transakcji jest niższa, przez co średnie z RCN mają tendencję
                                    do zbliżania się do siebie. Minimalna różnica nie musi oznaczać, że negocjacje są niemożliwe – raczej że dane nie pokazują
                                    wyraźnego marginesu.
                                </Text>
                            </div>
                        </div>
                    ) : null}
                </div>
            </Container>

            <PricesHubSchemaDataset rows={rows} summary={summary} offerMonthLabel={offerMonthLabel} />
        </section>
    );
};

const sectionStyle = css({
    pt: 6,
    pb: 2,
    backgroundColor: "var(--colors-white)",

    md: {
        pt: 9,
        pb: 3
    }
});

const innerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    maxWidth: "127.2rem",
    mx: "auto",

    md: {
        gap: 3
    }
});

const headingStyle = css({
    width: "100%",
    margin: 0,
    color: "var(--colors-secondary)",
    textAlign: "left",

    md: {
        textAlign: "center"
    }
});

const leadStyle = css({
    maxWidth: "90rem",
    margin: 0,
    color: "var(--colors-gray-700)"
});

const noteStyle = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    width: "100%"
});

const noteIconHolderStyle = css({
    display: "inline-flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem",
    color: "var(--colors-gray-700)"
});

const noteCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const noteTextStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const gapCityLinkStyle = css({
    color: "inherit",
    textDecoration: "underline",

    _hover: {
        color: "var(--colors-secondary)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
